/**
 * Validations bloquantes fiche produit — Lot 2 (source unique bouton + submit).
 */

import { PRODUCT_SECTION_KEYS as S } from '../config/produitSectionKeys.js';

export const PRODUIT_FORM_ISSUE_IDS = Object.freeze({
  NOM: 'nom',
  PRIX: 'prix',
  PRIX_PAR_PERSONNE: 'prix_par_personne',
  PRIX_PLATEAU_OU_CATALOGUE: 'prix_plateau_ou_catalogue',
  PRIX_AU_POIDS: 'prix_au_poids',
  LIMITE_TOTAL: 'limite_total',
});

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function parsePositivePrice(value) {
  const cleaned = String(value ?? '').replace(',', '.').trim();
  if (!cleaned) return null;
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

/**
 * Indique si le bloc prix fixe catalogue est requis à la sauvegarde.
 * @param {object} formData
 * @param {Array} taillesPlateaux
 */
export function isCataloguePrixRequired(formData, taillesPlateaux = []) {
  if (formData.venteAuPoids) return false;
  if (formData.isPlateauParPersonne) return false;
  if (formData.modeComposition) {
    const hasTaillePrix = taillesPlateaux.some(
      (t) => t.prix != null && t.prix !== '' && parsePositivePrice(t.prix) > 0
    );
    return !hasTaillePrix;
  }
  return true;
}

/**
 * @typedef {Object} ProduitFormBlockingIssue
 * @property {string} id
 * @property {string} message
 * @property {string} sectionKey
 * @property {string} [field]
 */

/**
 * Liste des blocages empêchant l'enregistrement (messages commerçant).
 * @param {object} formData
 * @param {Array} taillesPlateaux
 * @returns {ProduitFormBlockingIssue[]}
 */
export function getProduitFormBlockingIssues(formData, taillesPlateaux = []) {
  /** @type {ProduitFormBlockingIssue[]} */
  const issues = [];

  if (!formData.nom?.trim()) {
    issues.push({
      id: PRODUIT_FORM_ISSUE_IDS.NOM,
      message: 'Indiquez le nom du produit.',
      sectionKey: S.INFOS,
      field: 'nom',
    });
  }

  if (formData.venteAuPoids) {
    const p = parsePositivePrice(formData.prixAuPoids);
    if (p == null || p <= 0) {
      issues.push({
        id: PRODUIT_FORM_ISSUE_IDS.PRIX_AU_POIDS,
        message: 'Indiquez un prix au poids (€/kg ou unité choisie).',
        sectionKey: S.VENTE_AU_POIDS,
        field: 'prixAuPoids',
      });
    }
    return issues;
  }

  if (formData.isPlateauParPersonne) {
    const p = parsePositivePrice(formData.prixParPersonne);
    if (p == null || p <= 0) {
      issues.push({
        id: PRODUIT_FORM_ISSUE_IDS.PRIX_PAR_PERSONNE,
        message: 'Indiquez un prix par personne pour cette planche.',
        sectionKey: S.PLANCHE_PERSONNES,
        field: 'prixParPersonne',
      });
    }
    return issues;
  }

  if (formData.modeComposition) {
    if (isCataloguePrixRequired(formData, taillesPlateaux)) {
      const p = parsePositivePrice(formData.prix);
      if (p == null || p <= 0) {
        issues.push({
          id: PRODUIT_FORM_ISSUE_IDS.PRIX_PLATEAU_OU_CATALOGUE,
          message:
            'Ajoutez un prix dans « Tailles disponibles », ou renseignez le prix de base.',
          sectionKey: S.COMPOSITION_PIECES,
          field: 'prix',
        });
      }
    }

    if (
      formData.validationMode !== 'FLEXIBLE' &&
      !formData.limiteTotal &&
      (!taillesPlateaux || taillesPlateaux.length === 0)
    ) {
      issues.push({
        id: PRODUIT_FORM_ISSUE_IDS.LIMITE_TOTAL,
        message:
          'Indiquez une limite de pièces, ou ajoutez des tailles de plateau (validation stricte).',
        sectionKey: S.COMPOSITION_PIECES,
        field: 'limiteTotal',
      });
    }

    return issues;
  }

  const p = parsePositivePrice(formData.prix);
  if (p == null || p <= 0) {
    issues.push({
      id: PRODUIT_FORM_ISSUE_IDS.PRIX,
      message: 'Indiquez un prix de vente (€).',
      sectionKey: S.PRIX,
      field: 'prix',
    });
  }

  return issues;
}

/**
 * @param {object} formData
 * @param {Array} taillesPlateaux
 */
export function canSubmitProduitForm(formData, taillesPlateaux = []) {
  return getProduitFormBlockingIssues(formData, taillesPlateaux).length === 0;
}

/**
 * @param {string} sectionKey
 */
export function scrollToProduitSection(sectionKey) {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-produit-section="${sectionKey}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
